import { useRef } from 'react';
import { motion, useInView } from 'framer-motion';

const fashionImage = "https://images.unsplash.com/photo-1654524196886-922711cab064?w=1200&q=75&fm=webp&fit=crop";

const fashionBrands = [
{ name: 'HG Textile Company', tagline: 'Premium Fabrics' },
{ name: 'Hunar', tagline: 'Luxury Ethnic Couture' },
{ name: 'Vastram', tagline: 'Accessible Ethnic Wear' },
{ name: 'RAGS', tagline: 'Sustainable Fashion' },
{ name: 'Heer', tagline: 'Jewellery for Women' },
{ name: 'Huzur', tagline: 'Accessories for Men' }];


const cultureBrands = [
{ name: 'Hemraj Talkies', tagline: 'Films & Content' },
{ name: "Hemraj Black Box", tagline: 'Private Screening Rooms' },
{ name: 'Row House Pvt. Ltd.', tagline: "Networking and Art - Invested" },
{ name: "Dubskool", tagline: 'Voice Infrastructure' }];


export const FashionCulture = () => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-100px" });

  return (
    <section
      id="fashion"
      data-testid="fashion-culture-section"
      ref={ref}
      className="dark-section py-24 lg:py-32">

      <div className="max-w-[1400px] mx-auto px-6 lg:px-12">
        {/* Header */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-24 mb-16 lg:mb-24">
          <div>
            <motion.p
              className="overline mb-4 !text-[#E7E7E7]"
              initial={{ opacity: 0, y: 20 }}
              animate={isInView ? { opacity: 1, y: 0 } : {}}
              transition={{ duration: 0.6 }}>

              Fashion, Jewellery & Culture
            </motion.p>

            <motion.h2
              data-testid="fashion-culture-title"
              className="font-heading text-3xl sm:text-4xl lg:text-5xl font-light tracking-tight text-[#F9F6F0]"
              initial={{ opacity: 0, y: 20 }}
              animate={isInView ? { opacity: 1, y: 0 } : {}}
              transition={{ duration: 0.6, delay: 0.1 }}>

              Shaping How People Present Themselves
            </motion.h2>

            <motion.p
              className="mt-6 text-base text-[#F9F6F0]/70 leading-relaxed"
              initial={{ opacity: 0, y: 20 }}
              animate={isInView ? { opacity: 1, y: 0 } : {}}
              transition={{ duration: 0.6, delay: 0.2 }}>

              Beyond spaces, Hemraj Grandsons shapes identities through textiles, fashion, 
              jewellery, and culture. We believe culture is as important as infrastructure.
            </motion.p>
          </div>

          <motion.div
            className="image-zoom"
            initial={{ opacity: 0, scale: 0.95 }}
            animate={isInView ? { opacity: 1, scale: 1 } : {}}
            transition={{ duration: 0.8, delay: 0.3 }}>

            <img
              src={fashionImage}
              alt="Hemraj Grandsons Lifestyle"
              className="w-full aspect-[4/3] object-cover" />

          </motion.div>
        </div>

        {/* Fashion Brands Marquee */}
        <div className="mb-16 lg:mb-24">
          <motion.p
            className="overline mb-8 text-[#F9F6F0]/60"
            initial={{ opacity: 0 }}
            animate={isInView ? { opacity: 1 } : {}}
            transition={{ duration: 0.6, delay: 0.4 }}>

            Fashion & Jewellery
          </motion.p>

          <div className="marquee-container py-4">
            <div className="marquee-content">
              {[...fashionBrands, ...fashionBrands].map((brand, index) =>
              <div
                key={`${brand.name}-${index}`}
                data-testid={`fashion-brand-${brand.name.toLowerCase().replace(/\s+/g, '-')}`}
                className="inline-flex items-center mx-8 lg:mx-12">

                  <span className="font-heading text-2xl lg:text-3xl font-light text-[#F9F6F0]">
                    {brand.name}
                  </span>
                  <span className="ml-4 text-sm text-[#C5A880]">
                    {brand.tagline}
                  </span>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Culture Section */}
        <div>
          <motion.p
            className="overline mb-8 text-[#F9F6F0]/60"
            initial={{ opacity: 0 }}
            animate={isInView ? { opacity: 1 } : {}}
            transition={{ duration: 0.6, delay: 0.5 }}>

            Media & Culture
          </motion.p>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mb-8">
            {/* Hemraj Black Box Feature */}
            <motion.div
              className="relative overflow-hidden"
              initial={{ opacity: 0, y: 20 }}
              animate={isInView ? { opacity: 1, y: 0 } : {}}
              transition={{ duration: 0.6, delay: 0.5 }}
            >
              <img
                src="https://images.unsplash.com/photo-1771574203200-0ec88f162fe0?w=800&q=75&fm=webp&fit=crop"
                alt="Hemraj Black Box - Private Cinema"
                className="w-full aspect-video object-cover"
                loading="lazy"
              />
              <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/80 to-transparent p-6">
                <h3 className="font-heading text-2xl font-light text-[#F9F6F0]">Hemraj Black Box</h3>
                <p className="text-sm text-[#F9F6F0]/70 mt-1">Private Screening Rooms</p>
              </div>
            </motion.div>

            {/* Culture Brands Grid */}
            <div className="grid grid-cols-2 gap-4">
              {cultureBrands.map((brand, index) =>
              <motion.div
                key={brand.name}
                data-testid={`culture-brand-${brand.name.toLowerCase().replace(/\s+/g, '-')}`}
                className="border border-[#F9F6F0]/20 p-6 transition-all duration-500 hover:border-[#C5A880] hover:bg-[#F9F6F0]/5 cursor-pointer group"
                initial={{ opacity: 0, y: 20 }}
                animate={isInView ? { opacity: 1, y: 0 } : {}}
                transition={{ duration: 0.5, delay: 0.6 + index * 0.1 }}>

                  <h3 className="font-heading group-hover:text-[#C5A880] transition-colors text-lg font-medium !text-[#F9F6F0]">
                    {brand.name}
                  </h3>
                  <p className="mt-2 text-sm font-medium text-[#F9F6F0]/70">
                    {brand.tagline}
                  </p>
                </motion.div>
              )}
            </div>
          </div>
        </div>

        {/* Quote */}
        <motion.div
          className="mt-24 text-center max-w-3xl mx-auto"
          initial={{ opacity: 0, y: 30 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.8, delay: 0.8 }}>

          <blockquote className="font-heading text-2xl lg:text-3xl font-light text-[#F9F6F0] italic leading-relaxed">
            "Together, these brands allow Hemraj Grandsons to shape not only where people live, 
            but how they present themselves."
          </blockquote>
        </motion.div>
      </div>
    </section>);

};